// Mind2Magic CRM — Guide Planner, Trip View, Team & Viajeros Module
// Extracted from index.html Step 12.

import { STAGES, TYPE_LABELS } from './constants.js'
import { store } from './store.js'

let currentViajeroLeadId = null

function generateGuideToken() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let token = ''
    const arr = new Uint8Array(12)
    crypto.getRandomValues(arr)
    for (let i = 0; i < 12; i++) token += chars[arr[i] % chars.length]
    return token
}

function determineParkCodes(l) {
    const fd = l.formData || {}
    // Check explicit park selections from MagicForm
    if (fd.selectedParks) {
        const map = { mk: 'mk', ep: 'ep', hs: 'hs', ak: 'ak' }
        return Object.keys(fd.selectedParks).filter(k => fd.selectedParks[k] && map[k]).map(k => map[k])
    }
    if (['disney', 'disney_universal', 'parques_crucero'].includes(l.type)) return ['mk', 'hs', 'ep', 'ak']
    if (l.type === 'universal') return []
    return []
}

function extractMinorAges(l) {
    // From grupoViajeros (most structured)
    if (l.grupoViajeros?.length) {
        return l.grupoViajeros.filter(v => v.esMenor && v.edad).map(v => Number(v.edad)).filter(a => a > 0)
    }
    const fd = l.formData || {}
    // From Typeform edadesMenores string "8, 7, 5"
    if (typeof fd.edadesMenores === 'string' && fd.edadesMenores.trim()) {
        return fd.edadesMenores.split(/[,;]+/).map(s => parseInt(s.trim())).filter(n => n > 0 && n < 18)
    }
    // From MagicForm minors array
    if (Array.isArray(fd.minors)) {
        return fd.minors.map(m => typeof m === 'object' ? m.age : Number(m)).filter(n => n > 0 && n < 18)
    }
    if (Array.isArray(fd.edadesMenores)) {
        return fd.edadesMenores.map(Number).filter(n => n > 0 && n < 18)
    }
    return []
}

function _guideCounter(id, min, max, initial) {
    const val = initial != null ? Math.max(min, Math.min(max, initial)) : min
    return `<div style="display:flex;align-items:center;gap:8px;">
        <button type="button" onclick="const el=document.getElementById('${id}');const v=Math.max(${min},parseInt(el.value||${min})-1);el.value=v;" style="width:32px;height:32px;border-radius:8px;border:1px solid var(--border);background:var(--bg-2);color:var(--text-1);font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;">−</button>
        <input id="${id}" type="number" value="${val}" min="${min}" max="${max}" style="width:48px;height:32px;text-align:center;border-radius:8px;border:1px solid var(--border);background:var(--bg-2);color:var(--text-1);font-size:16px;font-weight:600;font-family:inherit;-moz-appearance:textfield;" oninput="this.value=Math.min(${max},Math.max(${min},parseInt(this.value)||${min}))">
        <button type="button" onclick="const el=document.getElementById('${id}');const v=Math.min(${max},parseInt(el.value||${min})+1);el.value=v;" style="width:32px;height:32px;border-radius:8px;border:1px solid var(--border);background:var(--bg-2);color:var(--text-1);font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;">+</button>
        <span style="font-size:12px;color:var(--text-3);">días</span>
    </div>`
}

function generateGuideLink(leadId) {
    const { leads, historico } = store
    const l = leads.find(x => x.id === leadId) || historico.find(x => x.id === leadId)
    if (!l) return window.toast('Lead no encontrado', 'error')
    if (!l._supabaseId) return window.toast('Lead no sincronizado con Supabase', 'error')

    const fd = l.formData || {}
    const dest = (l.destination || '').toLowerCase()
    // Detect from parkEntries: check park names for Disney/Universal keywords
    const parkNames = (l.parkEntries || []).map(pe => (pe.park || '').toLowerCase())
    const hasDisneyTickets = parkNames.some(p => p.includes('disney') || p.includes('magic kingdom') || p.includes('epcot') || p.includes('hollywood studios') || p.includes('animal kingdom'))
    const hasUniversalTickets = parkNames.some(p => p.includes('universal') || p.includes('island') || p.includes('epic universe'))
    const hasDis = ['disney', 'disney_universal', 'parques_crucero'].includes(l.type) || dest.includes('disney') || fd.diasDisney > 0 || fd.parquesMarca === 'disney' || fd.parquesMarca === 'ambos' || hasDisneyTickets
    const hasUni = ['universal', 'disney_universal', 'parques_crucero'].includes(l.type) || dest.includes('universal') || fd.diasUniversal > 0 || fd.parquesMarca === 'universal' || fd.parquesMarca === 'ambos' || hasUniversalTickets
    const showBoth = !hasDis && !hasUni

    const disneyParks = [
        { code: 'mk', name: 'Magic Kingdom', icon: '\u{1F3F0}' },
        { code: 'hs', name: 'Hollywood Studios', icon: '\u{1F3AC}' },
        { code: 'ep', name: 'EPCOT', icon: '\u{1F30D}' },
        { code: 'ak', name: 'Animal Kingdom', icon: '\u{1F981}' },
    ]
    const universalParks = [
        { code: 'uso', name: 'Universal Studios', icon: '\u{1F3A5}' },
        { code: 'ioa', name: 'Islands of Adventure', icon: '\u{1F3DD}' },
        { code: 'epic', name: 'Epic Universe', icon: '\u{1F680}' },
    ]

    const hasHotelInfo = !!(fd.hospedaje || fd.tipoHotel || l.disneyHotel)
    const defaultDisDays = hasHotelInfo ? 2 : 3
    const initDisDays = parseInt(l.diasParque) || parseInt(fd.diasDisney) || defaultDisDays
    const initUniDays = parseInt(fd.diasUniversal) || 2
    const initHopper = l.parkHopper || fd.parkHopper || false
    const initP2P = fd.parkTopark || fd.parkTopark || false

    let html = '<div style="padding:4px 0;">'
    html += `<p style="font-size:13px;color:var(--text-2);margin-bottom:16px;">Configura el itinerario de <strong>${window.escHtml(l.titular)}</strong>:</p>`

    // Disney section
    const disChecked = hasDis || showBoth
    if (hasDis || hasUni || showBoth) {
        html += `<div id="guideDisneySection" style="margin-bottom:20px;">`
        html += `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;">
                <input type="checkbox" id="guideToggleDisney" ${disChecked ? 'checked' : ''} onchange="document.getElementById('guideDisneyContent').style.display=this.checked?'block':'none'" style="width:15px;height:15px;">
                <span style="font-size:12px;font-weight:600;color:var(--text-3);text-transform:uppercase;">Disney World</span>
            </label>`
        html += _guideCounter('guideDays_disney', 1, 99, initDisDays)
        html += '</div>'
        html += `<div id="guideDisneyContent" style="${disChecked ? '' : 'display:none;'}">`
        html += `<label style="display:flex;align-items:center;gap:8px;padding:6px 12px;background:var(--bg-2);border-radius:8px;margin-bottom:10px;cursor:pointer;border:1px solid var(--border);">
            <input type="checkbox" id="guideHopper" ${initHopper ? 'checked' : ''} style="width:15px;height:15px;">
            <span style="font-size:12px;color:var(--text-2);">🎟️ Park Hopper <span style="opacity:0.6;">(varios parques por día)</span></span>
        </label>`
        html += '<div style="font-size:11px;color:var(--text-3);margin-bottom:6px;">Parques específicos <span style="opacity:0.6;">(opcional — si no seleccionas, el viajero elige)</span></div>'
        disneyParks.forEach(p => {
            html += `<label style="display:flex;align-items:center;gap:10px;padding:7px 12px;background:var(--bg-2);border-radius:8px;margin-bottom:3px;cursor:pointer;">
                <input type="checkbox" class="guide-park-cb" data-code="${p.code}" data-brand="disney" style="width:16px;height:16px;">
                <span style="font-size:13px;">${p.icon} ${p.name}</span>
            </label>`
        })
        html += '</div></div>'
    }

    // Universal section
    const uniChecked = hasUni || showBoth
    {
        html += `<div id="guideUniversalSection" style="margin-bottom:20px;">`
        html += `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;">
                <input type="checkbox" id="guideToggleUniversal" ${uniChecked ? 'checked' : ''} onchange="document.getElementById('guideUniversalContent').style.display=this.checked?'block':'none'" style="width:15px;height:15px;">
                <span style="font-size:12px;font-weight:600;color:var(--text-3);text-transform:uppercase;">Universal Orlando</span>
            </label>`
        html += _guideCounter('guideDays_universal', 1, 99, initUniDays)
        html += '</div>'
        html += `<div id="guideUniversalContent" style="${uniChecked ? '' : 'display:none;'}">`
        html += `<label style="display:flex;align-items:center;gap:8px;padding:6px 12px;background:var(--bg-2);border-radius:8px;margin-bottom:10px;cursor:pointer;border:1px solid var(--border);">
            <input type="checkbox" id="guideP2P" ${initP2P ? 'checked' : ''} style="width:15px;height:15px;">
            <span style="font-size:12px;color:var(--text-2);">🎟️ Park to Park <span style="opacity:0.6;">(varios parques por día)</span></span>
        </label>`
        html += '<div style="font-size:11px;color:var(--text-3);margin-bottom:6px;">Parques específicos <span style="opacity:0.6;">(opcional)</span></div>'
        universalParks.forEach(p => {
            html += `<label style="display:flex;align-items:center;gap:10px;padding:7px 12px;background:var(--bg-2);border-radius:8px;margin-bottom:3px;cursor:pointer;">
                <input type="checkbox" class="guide-park-cb" data-code="${p.code}" data-brand="universal" style="width:16px;height:16px;">
                <span style="font-size:13px;">${p.icon} ${p.name}</span>
            </label>`
        })
        html += '</div></div>'
    }

    html += `<button class="btn btn-primary btn-sm" onclick="confirmGenerateGuide('${l.id}')" style="width:100%;margin-top:8px;background:var(--gradient);border:none;color:white;">✨ Generar Itinerario</button>`
    html += '</div>'

    let overlay = document.getElementById('guideParkModal')
    if (!overlay) {
        overlay = document.createElement('div')
        overlay.id = 'guideParkModal'
        overlay.className = 'modal-bg'
        overlay.addEventListener('click', (e) => { if (e.target === overlay) window.closeModal('guideParkModal') })
        document.body.appendChild(overlay)
    }
    overlay.innerHTML = `<div class="modal" style="max-width:440px;">
        <div class="modal-head">
            <h2 class="modal-title">✨ Configurar Itinerario</h2>
            <button class="modal-close" onclick="closeModal('guideParkModal')">✕</button>
        </div>
        <div class="modal-body">${html}</div>
    </div>`
    window.openModal('guideParkModal')

    // Set initial counter values after DOM renders
    setTimeout(() => {
        const disEl = document.getElementById('guideDays_disney')
        const uniEl = document.getElementById('guideDays_universal')
        if (disEl) disEl.value = initDisDays
        if (uniEl) uniEl.value = initUniDays
    }, 50)
}

async function confirmGenerateGuide(leadId) {
    const { leads, historico, allGuideSessions } = store
    const l = leads.find(x => x.id === leadId) || historico.find(x => x.id === leadId)
    if (!l) return

    // Check which brands are enabled via toggles
    const disToggle = document.getElementById('guideToggleDisney')
    const uniToggle = document.getElementById('guideToggleUniversal')
    const disEnabled = disToggle ? disToggle.checked : false
    const uniEnabled = uniToggle ? uniToggle.checked : false

    // Collect days from counters (only if brand is enabled)
    const disEl = document.getElementById('guideDays_disney')
    const uniEl = document.getElementById('guideDays_universal')
    const disneyDays = disEnabled && disEl ? parseInt(disEl.value) || 0 : 0
    const universalDays = uniEnabled && uniEl ? parseInt(uniEl.value) || 0 : 0

    if (disneyDays === 0 && universalDays === 0) return window.toast('Selecciona al menos 1 día', 'error')

    // Collect selected parks from checkboxes (only enabled brands)
    const cbs = document.querySelectorAll('#guideParkModal .guide-park-cb:checked')
    const selectedParks = Array.from(cbs).filter(cb => {
        if (cb.dataset.brand === 'disney' && !disEnabled) return false
        if (cb.dataset.brand === 'universal' && !uniEnabled) return false
        return true
    }).map(cb => cb.dataset.code)

    window.closeModal('guideParkModal')

    const prefillData = {
        titular: l.titular, phone: l.phone, travelDate: l.travelDate,
        travelEnd: l.travelEnd, hotel: l.hotel, destination: l.destination,
        type: l.type, viajeros: l.viajeros, diasParque: l.diasParque,
        tipoParque: l.tipoParque, parkHopper: l.parkHopper || false,
        hasDiningPlan: l.hasDiningPlan || false,
        adultos: l.formData?.adultos || null,
        menores: l.formData?.menores || null,
        habitaciones: l.formData?.habitaciones || null,
    }
    const hopperEl = document.getElementById('guideHopper')
    const p2pEl = document.getElementById('guideP2P')
    const parkHopper = disEnabled && hopperEl ? hopperEl.checked : false
    const parkTopark = uniEnabled && p2pEl ? p2pEl.checked : false

    const config = {
        parks: selectedParks,
        disneyDays: disneyDays,
        universalDays: universalDays,
        parkHopper: parkHopper,
        parkTopark: parkTopark,
        minorAges: extractMinorAges(l),
        hasDiningPlan: l.hasDiningPlan || false,
        leadType: l.type,
    }

    // If guide already exists, mark old as superseded and create new one
    const isRegeneration = !!l._guideSession
    if (isRegeneration) {
        await window.sb.from('guide_sessions').update({ status: 'superseded' }).eq('id', l._guideSession.id)
    }

    const token = generateGuideToken()
    const { data, error } = await window.sb.from('guide_sessions').insert({
        token, lead_id: l._supabaseId, prefill_data: prefillData, config, status: 'pending',
    }).select('*').single()
    if (error) return window.toast('Error creando itinerario: ' + error.message, 'error')

    l._guideSession = data
    allGuideSessions.push(data)
    const action = isRegeneration ? 'regenerado' : 'creado'
    window.addHistorial(l, 'guia', `Itinerario personalizado ${action}`)
    window.syncLeadToSupabase(l)
    window.toast(`Itinerario personalizado ${action}`, 'success')
    window.openLead(l.id)
}

function copyGuideLink(url) {
    navigator.clipboard.writeText(url).then(() => window.toast('Link copiado', 'success')).catch(() => window.toast('Error copiando', 'error'))
}

function renderGuideSection(l) {
    const isParkType = ['disney', 'universal', 'disney_universal', 'parques_crucero'].includes(l.type)
    const hasParkTickets = l.parkEntries?.some(pe => pe.park)
    if (!isParkType && !hasParkTickets) return ''

    const gs = l._guideSession
    const hasDateRange = l.travelDate && l.travelEnd
    if (!gs) {
        return `<div class="panel-section">
            <button class="btn btn-primary btn-sm" onclick="generateGuideLink('${l.id}')"
                style="width:100%;background:var(--gradient);border:none;color:white;">
                ✨ Generar Itinerario Personalizado
            </button>
            ${hasDateRange ? `<button id="parkRecBtn_${l.id}" class="btn btn-sm" onclick="fetchParkRecommendation('${l.id}')" style="width:100%;margin-top:6px;background:var(--gradient);border:none;color:white;">🎯 Días Recomendados</button>` : ''}
        </div>`
    }

    const statusLabel = { pending: 'Pendiente', viewed: 'Vista', in_progress: 'En progreso', completed: 'Completada', superseded: 'Reemplazada' }
    const statusColor = { pending: 'var(--text-3)', viewed: '#06b6d4', in_progress: '#f59e0b', completed: 'var(--green)', superseded: '#94a3b8' }
    const url = 'https://m2magic.com/guide/' + gs.token

    return `<div class="panel-section" style="border-left:3px solid ${statusColor[gs.status] || 'var(--text-3)'};">
        <div class="panel-section-title" style="display:flex;justify-content:space-between;">
            <span>✨ Itinerario Personalizado</span>
            <span style="color:${statusColor[gs.status]};font-size:11px;font-weight:600;">${statusLabel[gs.status] || gs.status}</span>
        </div>
        <div style="display:flex;gap:6px;margin-top:8px;">
            <button class="btn btn-sm btn-secondary" onclick="copyGuideLink('${url}')" style="flex:1;">📋 Copiar Link</button>
            ${l.phone ? `<a href="${window.generarWhatsAppLink(l.phone, '¡Hola ' + (l.titular || '').split(' ')[0] + '! Te comparto un formulario cortito para personalizar tu itinerario: ' + url)}"
                target="_blank" class="btn btn-sm" style="flex:1;background:var(--green);color:white;border:none;text-decoration:none;text-align:center;">💬 WhatsApp</a>` : ''}
        </div>
        ${gs.status === 'completed' ? `<button class="btn btn-sm btn-ghost" onclick="viewGuideResponses('${l.id}')" style="width:100%;margin-top:6px;">📋 Ver Respuestas</button>
            <button id="parkRecBtn_${l.id}" class="btn btn-sm" onclick="fetchParkRecommendation('${l.id}')" style="width:100%;margin-top:4px;background:var(--gradient);border:none;color:white;">🎯 Días Recomendados</button>` : ''}
        <button class="btn btn-xs btn-ghost" onclick="generateGuideLink('${l.id}')" style="width:100%;margin-top:4px;font-size:10px;color:var(--text-3);">🔄 Regenerar</button>
    </div>`
}

async function viewGuideResponses(leadId) {
    const { leads, historico } = store
    const l = leads.find(x => x.id === leadId) || historico.find(x => x.id === leadId)
    if (!l?._guideSession) return
    const { data } = await window.sb.from('guide_sessions').select('responses').eq('id', l._guideSession.id).single()
    if (!data?.responses) return window.toast('Sin respuestas aún', 'info')

    const r = data.responses
    const PARK_NAMES = { mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom' }
    let html = '<div style="max-height:70vh;overflow-y:auto;padding:4px 0;">'

    // Confirmación
    if (r.confirmacion) html += `<div class="panel-section"><div class="panel-section-title">✅ Confirmación</div><p style="font-size:13px;color:var(--text-2);">${r.confirmacion === 'correcto' ? 'Todo correcto' : 'Necesita corrección: ' + window.escHtml(r.correccion || '')}</p></div>`

    // Estilo
    const ritmoLabels = { intenso: '🏃 Intenso', balanceado: '⚖️ Balanceado', relajado: '🌴 Relajado' }
    const finalLabels = { shows: '🎆 Shows nocturnos', hotel: '🏨 Hotel temprano', flexible: '🤷 Flexibles' }
    if (r.ritmo || r.finalDia || r.visionViaje) {
        html += '<div class="panel-section"><div class="panel-section-title">🧳 Estilo de Viaje</div>'
        if (r.ritmo) html += `<p style="font-size:13px;color:var(--text-2);">Ritmo: <strong>${ritmoLabels[r.ritmo] || r.ritmo}</strong></p>`
        if (r.finalDia) html += `<p style="font-size:13px;color:var(--text-2);">Final del día: <strong>${finalLabels[r.finalDia] || r.finalDia}</strong></p>`
        if (r.visionViaje) html += `<p style="font-size:13px;color:var(--text-2);margin-top:6px;font-style:italic;">"${window.escHtml(r.visionViaje)}"</p>`
        html += '</div>'
    }

    // Attractions per park
    Object.keys(PARK_NAMES).forEach(code => {
        const attrKey = `atracciones_${code}`
        const llKey = `ll_${code}`
        const showsKey = `shows_${code}`
        if (r[attrKey]?.length || r[llKey] || r[showsKey]?.length) {
            html += `<div class="panel-section"><div class="panel-section-title">🎢 ${PARK_NAMES[code]}</div>`
            if (r[attrKey]?.length) html += `<p style="font-size:13px;color:var(--text-2);">Atracciones: <strong>${r[attrKey].join(', ')}</strong></p>`
            if (r[showsKey]?.length) html += `<p style="font-size:13px;color:var(--text-2);">Shows: <strong>${r[showsKey].join(', ')}</strong></p>`
            const llLabels = { no: 'Sin LL', multipass: 'MultiPass', multipass_plus: 'MultiPass + Individual' }
            if (r[llKey]) html += `<p style="font-size:13px;color:var(--text-2);">Lightning Lane: <strong>${llLabels[r[llKey]] || r[llKey]}</strong></p>`
            html += '</div>'
        }
    })

    // Comida
    if (r.comida?.length || r.characterDining) {
        html += '<div class="panel-section"><div class="panel-section-title">🍽️ Comida</div>'
        if (r.comida?.length) html += `<p style="font-size:13px;color:var(--text-2);">Preferencias: <strong>${r.comida.join(', ')}</strong></p>`
        if (r.restricciones?.length) html += `<p style="font-size:13px;color:var(--text-2);">Restricciones: <strong>${r.restricciones.join(', ')}</strong></p>`
        if (r.alergiasDetalle) html += `<p style="font-size:13px;color:var(--text-2);">Alergias: ${window.escHtml(r.alergiasDetalle)}</p>`
        const cdLabels = { si: 'Sí', tal_vez: 'Tal vez', no: 'No' }
        if (r.characterDining) html += `<p style="font-size:13px;color:var(--text-2);">Comer con personajes: <strong>${cdLabels[r.characterDining] || r.characterDining}</strong></p>`
        html += '</div>'
    }

    // Momentos especiales
    if (r.viajeUnico || r.celebraciones?.length || r.experiencias?.length) {
        html += '<div class="panel-section"><div class="panel-section-title">💫 Momentos Especiales</div>'
        if (r.viajeUnico) html += `<p style="font-size:13px;color:var(--text-2);font-style:italic;">"${window.escHtml(r.viajeUnico)}"</p>`
        if (r.celebraciones?.length) html += `<p style="font-size:13px;color:var(--text-2);margin-top:4px;">Celebraciones: <strong>${r.celebraciones.join(', ')}</strong></p>`
        if (r.celebracionDetalle) html += `<p style="font-size:13px;color:var(--text-2);">Detalle: ${window.escHtml(r.celebracionDetalle)}</p>`
        if (r.experiencias?.length) html += `<p style="font-size:13px;color:var(--text-2);margin-top:4px;">Experiencias: <strong>${r.experiencias.join(', ')}</strong></p>`
        html += '</div>'
    }

    // Notas
    if (r.notasSara) {
        html += `<div class="panel-section"><div class="panel-section-title">📝 Notas para Sara</div><p style="font-size:13px;color:var(--text-2);font-style:italic;">"${window.escHtml(r.notasSara)}"</p></div>`
    }

    html += '</div>'

    // Create dynamic modal
    let overlay = document.getElementById('guideResponsesModal')
    if (!overlay) {
        overlay = document.createElement('div')
        overlay.id = 'guideResponsesModal'
        overlay.className = 'modal-bg'
        overlay.addEventListener('click', (e) => { if (e.target === overlay) window.closeModal('guideResponsesModal') })
        document.body.appendChild(overlay)
    }
    overlay.innerHTML = `<div class="modal" style="max-width:600px;">
        <div class="modal-head">
            <h2 class="modal-title">✨ Respuestas — ${window.escHtml(l.titular)}</h2>
            <button class="modal-close" onclick="closeModal('guideResponsesModal')">✕</button>
        </div>
        <div class="modal-body">${html}</div>
    </div>`
    window.openModal('guideResponsesModal')
}

const PARK_FULL_NAMES = {
    mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom',
    uso: 'Universal Studios', ioa: 'Islands of Adventure', epic: 'Epic Universe'
}
const DAY_NAMES_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
const MONTH_NAMES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

const BACKEND_URL = import.meta.env.VITE_API_URL || 'https://mind2magic-api.onrender.com'

async function fetchParkRecommendation(leadId) {
    const { leads, historico } = store
    const l = leads.find(x => x.id === leadId) || historico.find(x => x.id === leadId)
    if (!l) return

    // Gather data from lead + guide session config
    const gs = l._guideSession
    const config = gs?.config || {}
    const fd = l.formData || {}

    const startDate = l.travelDate
    const endDate = l.travelEnd
    if (!startDate || !endDate) {
        window.toast('El lead necesita fecha de viaje y fecha de regreso', 'info')
        return
    }

    // Build selected parks — prefer traveler's actual guide responses over Sara's config
    let selectedParks = []
    if (gs?.id && window.sb) {
        try {
            const { data: gsData } = await window.sb.from('guide_sessions').select('responses').eq('id', gs.id).single()
            const r = gsData?.responses
            if (r) {
                // 1. Explicit park picker selections from traveler
                if (r.selectedParks_disney?.length) selectedParks.push(...r.selectedParks_disney)
                if (r.selectedParks_universal?.length) selectedParks.push(...r.selectedParks_universal)
                // 2. If no picker (Sara sent specific parks), check which parks have attraction selections
                if (!selectedParks.length) {
                    const parkCodes = ['mk', 'hs', 'ep', 'ak', 'uso', 'ioa', 'epic']
                    for (const code of parkCodes) {
                        if (r[`atracciones_${code}`]?.length) selectedParks.push(code)
                    }
                }
            }
        } catch (e) { console.warn('Could not fetch guide responses for recommendation:', e) }
    }
    if (!selectedParks.length) selectedParks = config.parks || []
    if (!selectedParks.length) selectedParks = determineParkCodes(l)
    if (!selectedParks.length) selectedParks = ['mk', 'hs', 'ep', 'ak']

    const parkDays = (config.disneyDays || 0) + (config.universalDays || 0) || parseInt(fd.diasParque) || parseInt(fd.diasDisney) || 4
    const hasHopper = config.parkHopper || false

    // Detect Disney hotel
    let isDisneyGuest = false
    let disneyHotel = null
    if (l.hotels?.length) {
        const dh = l.hotels.find(h => h.provider?.toLowerCase()?.includes('disney'))
        if (dh) {
            isDisneyGuest = true
            disneyHotel = dh.name || null
        }
    }

    const adults = parseInt(l.adultos) || parseInt(fd.adultos) || 2
    const minorAges = extractMinorAges(l)

    // Show loading state
    const btn = document.getElementById('parkRecBtn_' + leadId)
    if (btn) { btn.disabled = true; btn.textContent = '⏳ Calculando...' }

    try {
        const resp = await fetch(BACKEND_URL + '/recommend-parks', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                startDate, endDate, selectedParks, parkDays, hasHopper,
                isDisneyGuest, disneyHotel, adults,
                minors: minorAges.map((age, i) => ({ id: i, age })),
            }),
        })

        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}))
            throw new Error(err.detail || `Error ${resp.status}`)
        }

        const data = await resp.json()

        // Cache on lead
        l._parkRecommendation = data

        // Render the recommendation
        _renderParkRecommendation(l, data)
    } catch (e) {
        console.error('Park recommendation error:', e)
        window.toast('Error obteniendo recomendación: ' + e.message, 'error')
        if (btn) { btn.disabled = false; btn.textContent = '🎯 Días Recomendados' }
    }
}

function _renderParkRecommendation(l, data) {
    const { assignments, parkHours, crowdLevels, approximate } = data

    // Build sortable entries
    const entries = Object.entries(assignments).sort(([a], [b]) => a.localeCompare(b))

    // Build copy text
    let textLines = [`🎯 Días Recomendados para ${l.titular}\n`]
    let htmlRows = ''

    for (const [dateStr, parks] of entries) {
        const d = new Date(dateStr + 'T12:00:00')
        const dayName = DAY_NAMES_ES[d.getDay()]
        const monthName = MONTH_NAMES_ES[d.getMonth()]
        const parkNames = parks.map(p => PARK_FULL_NAMES[p] || p).join(' + ')

        const hours = parkHours?.[dateStr] || {}
        const firstPark = parks[0]
        const h = hours[firstPark] || {}
        const timeStr = h.open && h.close ? ` (${h.open} - ${h.close})` : ''

        const crowd = crowdLevels?.[dateStr] || {}
        const rawCrowd = crowd[firstPark]
        const crowdVal = rawCrowd != null ? (rawCrowd > 10 ? rawCrowd / 10 : rawCrowd) : null
        const crowdEmoji = crowdVal != null ? (crowdVal <= 3 ? '🟢' : crowdVal <= 6 ? '🟡' : '🔴') : ''
        const crowdLabel = crowdVal != null ? ` ${crowdEmoji} ${crowdVal.toFixed(1)}/10` : ''

        textLines.push(`📅 ${dayName} ${d.getDate()} ${monthName} — ${parkNames}${timeStr}`)

        htmlRows += `<div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border);">
            <span style="font-size:20px;">📅</span>
            <div style="flex:1;">
                <div style="font-weight:600;color:var(--text-1);">${dayName} ${d.getDate()} ${monthName}</div>
                <div style="font-size:13px;color:var(--text-2);">${window.escHtml(parkNames)}${timeStr}${crowdLabel}</div>
            </div>
            <input type="date" value="${dateStr}" data-park="${parks.join(',')}"
                style="font-size:12px;padding:2px 4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-1);color:var(--text-1);width:130px;"
                class="rec-date-input" />
        </div>`
    }

    textLines.push(`\nBasado en horarios y niveles de multitud para tus fechas.`)
    if (approximate) textLines.push(`⚠️ Algunos horarios son aproximados (aún no publicados oficialmente).`)

    const copyText = textLines.join('\n')

    // Create or update the recommendation container
    let container = document.getElementById('parkRecResult_' + l.id)
    if (!container) {
        container = document.createElement('div')
        container.id = 'parkRecResult_' + l.id
        const btn = document.getElementById('parkRecBtn_' + l.id)
        if (btn) btn.parentElement.insertBefore(container, btn.nextSibling)
    }

    container.innerHTML = `
        <div style="margin-top:8px;padding:10px;background:var(--bg-2);border-radius:8px;border:1px solid var(--border);">
            <div style="font-weight:600;font-size:13px;color:var(--text-1);margin-bottom:6px;">🎯 Días Recomendados</div>
            ${htmlRows}
            ${approximate ? '<div style="font-size:11px;color:var(--text-3);margin-top:6px;">⚠️ Algunos horarios son aproximados</div>' : ''}
            <div style="display:flex;gap:6px;margin-top:8px;">
                <button class="btn btn-sm" onclick="recalcParkRecommendation('${l.id}')" style="flex:1;background:var(--purple);color:white;border:none;">🔄 Recalcular</button>
                <button class="btn btn-sm btn-secondary" onclick="copyParkRecommendation('${l.id}')" style="flex:1;">📋 Copiar</button>
                ${l.phone ? `<button class="btn btn-sm" onclick="whatsappParkRecommendation('${l.id}')" style="flex:1;background:var(--green);color:white;border:none;">💬 WhatsApp</button>` : ''}
            </div>
        </div>`

    // Store copy text on element
    container._copyText = copyText

    // Reset button
    const recBtn = document.getElementById('parkRecBtn_' + l.id)
    if (recBtn) { recBtn.disabled = false; recBtn.textContent = '🎯 Días Recomendados' }
}

async function recalcParkRecommendation(leadId) {
    const { leads, historico } = store
    const l = leads.find(x => x.id === leadId) || historico.find(x => x.id === leadId)
    if (!l) return

    const container = document.getElementById('parkRecResult_' + leadId)
    if (!container) return

    // Read edited dates from the date inputs
    const inputs = container.querySelectorAll('.rec-date-input')
    const forcedAssignments = {}
    for (const input of inputs) {
        const dateVal = input.value
        const parks = input.dataset.park.split(',')
        if (dateVal && parks.length) forcedAssignments[dateVal] = parks
    }

    if (!Object.keys(forcedAssignments).length) return

    // Gather same params as original request
    const gs = l._guideSession
    const config = gs?.config || {}
    const fd = l.formData || {}
    const startDate = l.travelDate
    const endDate = l.travelEnd

    let isDisneyGuest = false
    let disneyHotel = null
    if (l.hotels?.length) {
        const dh = l.hotels.find(h => h.provider?.toLowerCase()?.includes('disney'))
        if (dh) { isDisneyGuest = true; disneyHotel = dh.name || null }
    }

    const adults = parseInt(l.adultos) || parseInt(fd.adultos) || 2
    const minorAges = extractMinorAges(l)
    const selectedParks = [...new Set(Object.values(forcedAssignments).flat())]

    // Show loading
    const recalcBtn = container.querySelector('[onclick*="recalcParkRecommendation"]')
    if (recalcBtn) { recalcBtn.disabled = true; recalcBtn.textContent = '⏳ Recalculando...' }

    try {
        const resp = await fetch(BACKEND_URL + '/recommend-parks', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                startDate, endDate, selectedParks,
                parkDays: Object.keys(forcedAssignments).length,
                hasHopper: config.parkHopper || false,
                isDisneyGuest, disneyHotel, adults,
                minors: minorAges.map((age, i) => ({ id: i, age })),
                forcedAssignments,
            }),
        })

        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}))
            throw new Error(err.detail || `Error ${resp.status}`)
        }

        const data = await resp.json()
        l._parkRecommendation = data
        _renderParkRecommendation(l, data)
        window.toast('Horarios actualizados', 'success')
    } catch (e) {
        console.error('Recalc error:', e)
        window.toast('Error recalculando: ' + e.message, 'error')
        if (recalcBtn) { recalcBtn.disabled = false; recalcBtn.textContent = '🔄 Recalcular' }
    }
}

function copyParkRecommendation(leadId) {
    const container = document.getElementById('parkRecResult_' + leadId)
    if (container?._copyText) {
        navigator.clipboard.writeText(container._copyText)
            .then(() => window.toast('Recomendación copiada', 'success'))
            .catch(() => window.toast('Error copiando', 'error'))
    }
}

function whatsappParkRecommendation(leadId) {
    const { leads, historico } = store
    const l = leads.find(x => x.id === leadId) || historico.find(x => x.id === leadId)
    const container = document.getElementById('parkRecResult_' + leadId)
    if (!l?.phone || !container?._copyText) return
    const url = window.generarWhatsAppLink(l.phone, container._copyText)
    window.open(url, '_blank')
}

function renderFormDataPanel(l) {
    const fd = l.formData && typeof l.formData === 'object' ? l.formData : null
    if (!fd && !l.observaciones) return ''
    // Structured display from formData
    if (fd) {
        const rows = []
        const add = (icon, label, val) => { if (val && val !== 'nan') rows.push(`<div style="display:flex;gap:6px;margin-bottom:4px;"><span style="width:14px;text-align:center;">${icon}</span><span style="color:var(--text-3);min-width:100px;">${label}</span><span style="color:var(--text-1);font-weight:500;">${window.escHtml(String(val))}</span></div>`) }
        add('👥', 'Viajeros', fd.viajeros)
        add('👶', 'Edades menores', fd.edadesMenores)
        add('🏨', 'Habitaciones', window.fmtHabitaciones(fd))
        add('🚢', 'Camarotes', fd.cabinas)
        add('📅', 'Meses tentativo', fd.mesAproximado && (Array.isArray(fd.mesAproximado) ? fd.mesAproximado.join(', ') : fd.mesAproximado) + (fd.anioAproximado ? ' ' + fd.anioAproximado : ''))
        add('🗓️', 'Temporada', fd.temporadaFlexible ? (Array.isArray(fd.temporadaFlexible) ? fd.temporadaFlexible.join(', ') : fd.temporadaFlexible) + (fd.anioAproximado ? ' ' + fd.anioAproximado : '') : '')
        add('🎢', 'Marca parques', fd.parquesMarca)
        add('🏰', 'Destino Disney', fd.disneyDestino)
        add('🎬', 'Destino Universal', fd.universalDestino)
        add('🎢', 'Días Disney', fd.diasDisney)
        add('🎡', 'Días Universal', fd.diasUniversal)
        add('🎠', 'Parques Universal', fd.parquesUniversal)
        add('✨', 'Primera vez Disney', fd.primeraVezDisney != null ? (fd.primeraVezDisney ? 'Sí' : 'No') : '')
        add('✨', 'Primera vez Universal', fd.primeraVezUniversal != null ? (fd.primeraVezUniversal ? 'Sí' : 'No') : '')
        add('🏩', 'Hotel preferido', fd.hotel)
        add('🏷️', 'Tipo hotel', fd.tipoHotel)
        add('🏩', 'Hospedaje', Array.isArray(fd.hospedaje) ? fd.hospedaje.join(', ') : fd.hospedaje)
        add('🌙', 'Noches', fd.noches)
        add('🛏️', 'Habitaciones', window.fmtHabitaciones(fd))
        add('💰', 'Presupuesto', fd.presupuesto)
        add('🎪', 'Otras experiencias', Array.isArray(fd.otrasExperiencias) ? fd.otrasExperiencias.join(', ') : fd.otrasExperiencias)
        add('🎉', 'Evento especial', fd.eventoEspecial)
        add('🎪', 'Experiencias', fd.experiencias)
        add('📄', 'Documentos', fd.documentos)
        add('✈️', 'Tiquetes', fd.tiquetes)
        add('🏠', 'Residente FL', fd.residenteFL || fd.residenteFlorida)
        // Cruceros-specific
        add('⚓', 'Puertos', fd.puertos)
        add('🗺️', 'Destinos crucero', fd.destinos)
        add('⏱️', 'Duración', fd.duracion)
        add('🚢', 'Naviera', fd.naviera)
        add('🛏️', 'Cabinas', fd.cabinas)
        add('🌍', 'Residencia', fd.residencia)
        // Hoteles-specific
        add('📍', 'Destino', fd.destino)
        add('📅', 'Tipo fecha', fd.tipoFecha)
        add('📆', 'Rango fechas', fd.rangoFechas)
        add('🔧', 'Req. especial', fd.requerimientoEspecial)
        // Explorando-specific
        add('💡', 'Intereses', fd.intereses)
        add('📅', 'Cuándo viaja', fd.cuandoViaja)
        // Common
        add('📝', 'Notas', fd.notas)
        add('📣', 'Fuente', fd.fuente)
        if (!rows.length) return l.observaciones ? `<div style="margin-top:12px;padding:12px;background:var(--bg-2);border-radius:var(--radius);border-left:3px solid var(--cyan);"><div style="font-size:11px;font-weight:600;color:var(--cyan);margin-bottom:6px;">📋 Datos del Formulario</div><div style="font-size:12px;color:var(--text-2);white-space:pre-line;">${window.escHtml(l.observaciones)}</div></div>` : ''
        return `<div style="margin-top:12px;padding:12px;background:var(--bg-2);border-radius:var(--radius);border-left:3px solid var(--cyan);"><div style="font-size:11px;font-weight:600;color:var(--cyan);margin-bottom:6px;">📋 Datos del Formulario</div><div style="font-size:12px;">${rows.join('')}</div></div>`
    }
    // Fallback: raw observaciones
    return `<div style="margin-top:12px;padding:12px;background:var(--bg-2);border-radius:var(--radius);border-left:3px solid var(--cyan);"><div style="font-size:11px;font-weight:600;color:var(--cyan);margin-bottom:6px;">📋 Datos del Formulario</div><div style="font-size:12px;color:var(--text-2);white-space:pre-line;">${window.escHtml(l.observaciones)}</div></div>`
}

function openClient(key) {
    const { clients, leads, historico } = store
    const c = clients[key]; if (!c) return
    store.currentLeadId = null
    const totalVal = c.trips.reduce((s, t) => s + (t.totalVenta || 0), 0)
    const totalComm = c.trips.reduce((s, t) => s + (t.commission || 0), 0)
    const totalM2M = c.trips.reduce((s, t) => s + (t.commission70 || 0), 0)
    const isFreq = window.isFrequentTraveler(c)
    const isM2M = window.isM2MTraveler(c)

    document.getElementById('panelTitle').textContent = c.titular
    document.getElementById('btnEditPanel').classList.add('hidden')
    document.getElementById('panelBody').innerHTML = `
        <div class="panel-section"><div class="panel-section-title">Contacto</div>
            <div class="info-row"><span class="info-label">Titular</span><span class="info-val">${c.titular}</span></div>
            ${c.contacto ? `<div class="info-row"><span class="info-label">Contacto</span><span class="info-val">${c.contacto}</span></div>` : ''}
            <div class="info-row"><span class="info-label">Teléfono</span><span class="info-val">${c.phone || 'Sin registrar'}</span></div>
            <div class="info-row"><span class="info-label">Email</span><span class="info-val">${c.email || 'Sin registrar'}</span></div>
            ${c.referido ? `<div class="info-row"><span class="info-label">Referido</span><span class="info-val">${c.referido}</span></div>` : ''}
            ${isFreq ? `<div class="info-row"><span class="info-label">Estado</span><span class="info-val" style="color:var(--purple-light)">⭐ Viajero Frecuente</span></div>` : ''}
            ${isM2M ? `<div class="info-row"><span class="info-label">Estado</span><span class="info-val" style="color:var(--yellow)">💰 Viajero M2M</span></div>` : ''}
        </div>
        <div class="panel-section"><div class="panel-section-title">Resumen — ${c.trips.length} viajes</div>
            <div class="info-row"><span class="info-label">Valor Ventas</span><span class="info-val" style="color:var(--green)">${window.fmt(totalVal)}</span></div>
            <div class="info-row"><span class="info-label">Comisión Total</span><span class="info-val">${window.fmt(totalComm)}</span></div>
            <div class="info-row"><span class="info-label">Comisión M2M</span><span class="info-val" style="color:var(--green)">${window.fmt(totalM2M)}</span></div>
        </div>
        <div class="panel-section"><div class="panel-section-title">Historial</div>
            ${c.trips.map(t => `<div style="background:var(--bg-2);border-radius:var(--radius);padding:10px;margin-bottom:8px;">
                <div style="display:flex;justify-content:space-between;margin-bottom:4px;"><span style="font-weight:600;font-size:13px;">${t.destination}</span><span class="card-tag ${t.type}">${TYPE_LABELS[t.type] || 'Otro'}</span></div>
                <div style="font-size:11px;color:var(--text-3);margin-bottom:4px;">${window.fmtDate(t.travelDate)}</div>
                <div style="display:flex;justify-content:space-between;font-size:12px;"><span style="color:var(--green)">${window.fmt(t.totalVenta)}</span><span>${window.fmt(t.commission)}</span><span style="color:var(--green)">${window.fmt(t.commission70)}</span></div>
                <div style="font-size:10px;color:var(--text-3);margin-top:4px;">${STAGES.find(s => s.id === t.stage)?.name || t.stage}</div>
            </div>`).join('')}
        </div>
        ${(() => {
            // Find next upcoming trip
            const now = new Date()
            const upcomingTrips = c.trips.filter(t => t.travelDate && new Date(t.travelDate) >= now).sort((a,b) => new Date(a.travelDate) - new Date(b.travelDate))
            if (upcomingTrips.length > 0) {
                return `<div class="panel-section" style="display:flex;gap:8px;">
                    <button class="btn btn-primary btn-sm" onclick="openTripView('${key}', '${upcomingTrips[0].travelDate}')" style="flex:1;">🗺️ Ver Próximo Viaje</button>
                </div>`
            }
            return ''
        })()}
    `
    document.getElementById('panel').classList.add('active')
}

function closePanel() { document.getElementById('panel').classList.remove('active'); store.currentLeadId = null }

// Alias para openLead (usado en calendario y dashboard)
function openPanel(id) { window.openLead(id) }

// ========== TRIP VIEW (Viaje Completo) ==========
function openTripView(clientKey, baseDate) {
    const { clients, leads, historico } = store
    const c = clients[clientKey]
    if (!c) return

    // Get all leads for this client
    const clientLeads = leads.filter(l => l.clientKey === clientKey)
    const clientHistorico = historico.filter(h => h.clientKey === clientKey)
    const allTrips = [...clientLeads, ...clientHistorico]

    // If we have a base date, find trips within 30 days
    let relevantTrips = allTrips
    if (baseDate) {
        const base = new Date(baseDate)
        relevantTrips = allTrips.filter(t => {
            if (!t.travelDate) return false
            const tripDate = new Date(t.travelDate)
            const diffDays = Math.abs((tripDate - base) / (1000 * 60 * 60 * 24))
            return diffDays <= 30
        })
    }

    // Sort by travel date
    relevantTrips.sort((a, b) => new Date(a.travelDate || 0) - new Date(b.travelDate || 0))

    // Calculate totals
    const totalVenta = relevantTrips.reduce((s, t) => s + (t.totalVenta || 0), 0)
    const totalComm = relevantTrips.reduce((s, t) => s + (t.commission || 0), 0)
    const totalM2M = relevantTrips.reduce((s, t) => s + (t.commission70 || 0), 0)
    const totalDiasParque = relevantTrips.reduce((s, t) => s + (t.diasParque || 0), 0)

    // Get date range
    const dates = relevantTrips.filter(t => t.travelDate).map(t => new Date(t.travelDate))
    const endDates = relevantTrips.filter(t => t.travelEnd).map(t => new Date(t.travelEnd))
    const minDate = dates.length ? new Date(Math.min(...dates)) : null
    const maxDate = endDates.length ? new Date(Math.max(...endDates)) : (dates.length ? new Date(Math.max(...dates)) : null)

    document.getElementById('panelTitle').textContent = '🗺️ Viaje Completo'
    document.getElementById('btnEditPanel').classList.add('hidden')
    document.getElementById('panelBody').innerHTML = `
        <div class="panel-section">
            <div class="panel-section-title">Cliente</div>
            <div class="info-row"><span class="info-label">Titular</span><span class="info-val">${c.titular}</span></div>
            ${c.phone ? `<div class="info-row"><span class="info-label">Teléfono</span><span class="info-val">${c.phone}</span></div>` : ''}
        </div>
        <div class="panel-section">
            <div class="panel-section-title">📅 Período del Viaje</div>
            <div class="info-row"><span class="info-label">Inicio</span><span class="info-val">${minDate ? window.fmtDate(minDate.toISOString().split('T')[0]) : 'N/A'}</span></div>
            <div class="info-row"><span class="info-label">Fin</span><span class="info-val">${maxDate ? window.fmtDate(maxDate.toISOString().split('T')[0]) : 'N/A'}</span></div>
            ${totalDiasParque > 0 ? `<div class="info-row"><span class="info-label">Total Días Parque</span><span class="info-val" style="color:var(--purple-light);font-weight:600;">${totalDiasParque} días</span></div>` : ''}
        </div>
        <div class="panel-section">
            <div class="panel-section-title">💰 Resumen Financiero</div>
            <div class="info-row"><span class="info-label">Valor Total</span><span class="info-val" style="color:var(--green);font-weight:600;">${window.fmt(totalVenta)}</span></div>
            <div class="info-row"><span class="info-label">Comisión Total</span><span class="info-val">${window.fmt(totalComm)}</span></div>
            <div class="info-row"><span class="info-label">Comisión M2M</span><span class="info-val" style="color:var(--green)">${window.fmt(totalM2M)}</span></div>
        </div>
        <div class="panel-section">
            <div class="panel-section-title">📦 Componentes del Viaje (${relevantTrips.length})</div>
            ${relevantTrips.map(t => {
                const stageInfo = STAGES.find(s => s.id === t.stage)
                const stageColor = stageInfo?.color || '#888'
                return `
                <div style="background:var(--bg-2);border-radius:var(--radius);padding:12px;margin-bottom:8px;border-left:3px solid ${stageColor};">
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
                        <span style="font-weight:600;font-size:13px;">${t.destination || t.hotel || 'Sin destino'}</span>
                        <span class="card-tag ${t.type}" style="font-size:10px;">${TYPE_LABELS[t.type] || 'Otro'}</span>
                    </div>
                    <div style="font-size:11px;color:var(--text-3);margin-bottom:4px;">${window.fmtDate(t.travelDate)}${t.travelEnd ? ' → ' + window.fmtDate(t.travelEnd) : ''}</div>
                    ${t.diasParque ? `<div style="font-size:11px;color:var(--purple-light);margin-bottom:4px;">🎢 ${t.diasParque} días de parque${t.ticketValidFrom && t.ticketValidTo ? ` (${window.fmtDate(t.ticketValidFrom)} → ${window.fmtDate(t.ticketValidTo)})` : ''}</div>` : ''}
                    ${t.reservationNum ? `<div style="font-size:10px;color:var(--text-3);"># ${t.reservationNum}</div>` : ''}
                    <div style="display:flex;justify-content:space-between;font-size:12px;margin-top:6px;">
                        <span style="color:var(--green)">${window.fmt(t.totalVenta)}</span>
                        <span style="font-size:10px;color:${stageColor}">${stageInfo?.name || t.stage}</span>
                    </div>
                    <button class="btn btn-sm" onclick="openLead('${t.id}')" style="margin-top:8px;width:100%;font-size:11px;padding:4px;">Ver Detalle</button>
                </div>
                `
            }).join('')}
        </div>
        <div class="panel-section" style="display:flex;gap:8px;">
            <button class="btn btn-secondary btn-sm" onclick="openClient('${clientKey}')" style="flex:1;">👤 Ver Cliente</button>
        </div>
    `
    document.getElementById('panel').classList.add('active')
}

// ========== TEAM ==========
function saveTeamMember() {
    const { team } = store
    const nombre = document.getElementById('tNombre').value.trim()
    if (!nombre) { window.toast('Ingresa un nombre', 'error'); return }
    const member = { id: 't_' + Date.now(), nombre, rol: document.getElementById('tRol').value.trim(), email: document.getElementById('tEmail').value.trim(), orden: team.length + 1 }
    team.push(member)
    window.populateSelects(); window.renderTeam(); window.closeModal('newTeamModal')
    document.getElementById('tNombre').value = ''; document.getElementById('tRol').value = ''; document.getElementById('tEmail').value = ''
    // Sync to Supabase
    window.supabaseDB.upsertTeamMember(member).then(data => {
        if (data) { member._supabaseId = data.id; member.id = data.id }
    }).catch(err => { console.error('upsertTeamMember error:', err); window.toast('Error guardando en Supabase', 'error') })
    window.toast(`${nombre} agregado`, 'success')
}

function editTeamMember(id) {
    const { team } = store
    const m = team.find(t => t.id === id); if (!m) return
    document.getElementById('etId').value = id
    document.getElementById('etNombre').value = m.nombre
    document.getElementById('etRol').value = m.rol || ''
    document.getElementById('etEmail').value = m.email || ''
    window.openModal('editTeamModal')
}

function saveTeamEdit() {
    const { team, leads } = store
    const id = document.getElementById('etId').value, m = team.find(t => t.id === id); if (!m) return
    const oldName = m.nombre
    m.nombre = document.getElementById('etNombre').value.trim()
    m.rol = document.getElementById('etRol').value.trim()
    m.email = document.getElementById('etEmail').value.trim()
    if (oldName !== m.nombre) leads.forEach(l => { if (l.responsable === oldName) l.responsable = m.nombre })
    window.populateSelects(); window.renderTeam(); window.renderPipeline(); window.closeModal('editTeamModal')
    // Sync to Supabase
    window.supabaseDB.upsertTeamMember(m).catch(err => { console.error('upsertTeamMember error:', err); window.toast('Error guardando en Supabase', 'error') })
    window.toast('Actualizado', 'success')
}

function deleteTeamMember() {
    const { team } = store
    const id = document.getElementById('etId').value, idx = team.findIndex(t => t.id === id)
    if (idx === -1 || !confirm(`¿Eliminar a ${team[idx].nombre}?`)) return
    const member = team[idx]
    team.splice(idx, 1); window.populateSelects(); window.renderTeam(); window.closeModal('editTeamModal')
    // Soft-delete in Supabase
    if (member._supabaseId) window.supabaseDB.deleteTeamMember(member._supabaseId)
    window.toast('Eliminado', 'success')
}

// ========== VIAJEROS (TRAVELERS) ==========

function openEditViajeros(leadId) {
    const { leads } = store
    currentViajeroLeadId = leadId
    document.getElementById('vLeadId').value = leadId

    const l = leads.find(l => l.id === leadId)
    const { clients } = store
    const c = l ? clients[l.clientKey] : null
    const t = c ? c.trips.find(t => t.id === leadId) : null

    let viajeros = l?.grupoViajeros || t?.grupoViajeros || []

    // If no viajeros, create one from titular
    if (viajeros.length === 0 && l?.titular) {
        viajeros = [{ nombre: l.titular, esTitular: true, edad: null, fechaNacimiento: null, esMenor: false }]
    }

    renderViajerosList(viajeros)
    window.openModal('editViajerosModal')
}

function renderViajerosList(viajeros) {
    const container = document.getElementById('viajerosList')
    container.innerHTML = viajeros.map((v, idx) => `
        <div class="viajero-row" data-idx="${idx}" style="background:var(--bg-2);padding:12px;border-radius:8px;margin-bottom:8px;">
            <div class="form-row" style="grid-template-columns: 1fr 100px 140px 30px;gap:8px;">
                <div class="form-group" style="margin-bottom:0;">
                    <label class="form-label" style="font-size:10px;">Nombre ${idx === 0 ? '⭐' : ''}</label>
                    <input type="text" class="form-input" value="${v.nombre || ''}" data-field="nombre" placeholder="Nombre completo" style="font-size:12px;">
                </div>
                <div class="form-group" style="margin-bottom:0;">
                    <label class="form-label" style="font-size:10px;">Edad</label>
                    <input type="number" class="form-input" value="${v.edad || ''}" data-field="edad" placeholder="--" min="0" max="120" style="font-size:12px;" onchange="updateMenorStatus(this)">
                </div>
                <div class="form-group" style="margin-bottom:0;">
                    <label class="form-label" style="font-size:10px;">🎂 Nacimiento</label>
                    <input type="date" class="form-input" value="${v.fechaNacimiento || ''}" data-field="fechaNacimiento" style="font-size:11px;" onchange="calcAgeFromBirth(this)">
                </div>
                <div style="display:flex;align-items:flex-end;padding-bottom:2px;">
                    ${idx > 0 ? `<button type="button" class="btn btn-xs btn-ghost" onclick="removeViajeroRow(${idx})" style="color:var(--red);">✕</button>` : ''}
                </div>
            </div>
            <div style="display:flex;gap:12px;margin-top:6px;">
                <label style="display:flex;align-items:center;gap:4px;font-size:11px;cursor:pointer;">
                    <input type="checkbox" data-field="esMenor" ${v.esMenor ? 'checked' : ''} style="width:14px;height:14px;">
                    <span>Menor (M)</span>
                </label>
                <label style="display:flex;align-items:center;gap:4px;font-size:11px;cursor:pointer;">
                    <input type="checkbox" data-field="esTitular" ${v.esTitular ? 'checked' : ''} style="width:14px;height:14px;" onchange="updateTitular(this, ${idx})">
                    <span>Titular ⭐</span>
                </label>
            </div>
        </div>
    `).join('')
}

function addViajeroRow() {
    const container = document.getElementById('viajerosList')
    const rows = container.querySelectorAll('.viajero-row')
    const idx = rows.length

    const newRow = document.createElement('div')
    newRow.className = 'viajero-row'
    newRow.dataset.idx = idx
    newRow.style.cssText = 'background:var(--bg-2);padding:12px;border-radius:8px;margin-bottom:8px;'
    newRow.innerHTML = `
        <div class="form-row" style="grid-template-columns: 1fr 100px 140px 30px;gap:8px;">
            <div class="form-group" style="margin-bottom:0;">
                <label class="form-label" style="font-size:10px;">Nombre</label>
                <input type="text" class="form-input" value="" data-field="nombre" placeholder="Nombre completo" style="font-size:12px;">
            </div>
            <div class="form-group" style="margin-bottom:0;">
                <label class="form-label" style="font-size:10px;">Edad</label>
                <input type="number" class="form-input" value="" data-field="edad" placeholder="--" min="0" max="120" style="font-size:12px;" onchange="updateMenorStatus(this)">
            </div>
            <div class="form-group" style="margin-bottom:0;">
                <label class="form-label" style="font-size:10px;">🎂 Nacimiento</label>
                <input type="date" class="form-input" value="" data-field="fechaNacimiento" style="font-size:11px;" onchange="calcAgeFromBirth(this)">
            </div>
            <div style="display:flex;align-items:flex-end;padding-bottom:2px;">
                <button type="button" class="btn btn-xs btn-ghost" onclick="removeViajeroRow(${idx})" style="color:var(--red);">✕</button>
            </div>
        </div>
        <div style="display:flex;gap:12px;margin-top:6px;">
            <label style="display:flex;align-items:center;gap:4px;font-size:11px;cursor:pointer;">
                <input type="checkbox" data-field="esMenor" style="width:14px;height:14px;">
                <span>Menor (M)</span>
            </label>
            <label style="display:flex;align-items:center;gap:4px;font-size:11px;cursor:pointer;">
                <input type="checkbox" data-field="esTitular" style="width:14px;height:14px;" onchange="updateTitular(this, ${idx})">
                <span>Titular ⭐</span>
            </label>
        </div>
    `
    container.appendChild(newRow)
    newRow.querySelector('input[data-field="nombre"]').focus()
}

function removeViajeroRow(idx) {
    const rows = document.querySelectorAll('#viajerosList .viajero-row')
    if (rows[idx]) rows[idx].remove()
    // Re-index remaining rows
    document.querySelectorAll('#viajerosList .viajero-row').forEach((row, i) => {
        row.dataset.idx = i
    })
}

function updateTitular(checkbox, idx) {
    if (checkbox.checked) {
        // Uncheck all other titular checkboxes
        document.querySelectorAll('#viajerosList input[data-field="esTitular"]').forEach((cb, i) => {
            if (i !== idx) cb.checked = false
        })
    }
}

function calcAgeFromBirth(input) {
    const row = input.closest('.viajero-row')
    const edadInput = row.querySelector('input[data-field="edad"]')
    const menorCheckbox = row.querySelector('input[data-field="esMenor"]')

    if (input.value) {
        const birthDate = new Date(input.value)
        const today = new Date()
        let age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
        }
        edadInput.value = age
        menorCheckbox.checked = age < 18
    }
}

function updateMenorStatus(input) {
    const row = input.closest('.viajero-row')
    const menorCheckbox = row.querySelector('input[data-field="esMenor"]')
    const edad = parseInt(input.value)
    if (!isNaN(edad)) {
        menorCheckbox.checked = edad < 18
    }
}

function saveViajeros() {
    const { leads, clients, currentLeadId } = store
    const leadId = document.getElementById('vLeadId').value
    const l = leads.find(l => l.id === leadId)
    if (!l) { window.toast('Lead no encontrado', 'error'); return }

    // Save state for undo
    window.saveState('editar viajeros')

    const rows = document.querySelectorAll('#viajerosList .viajero-row')
    const viajeros = []

    rows.forEach((row, idx) => {
        const nombre = row.querySelector('input[data-field="nombre"]').value.trim()
        if (!nombre) return // Skip empty names

        viajeros.push({
            nombre,
            edad: parseInt(row.querySelector('input[data-field="edad"]').value) || null,
            fechaNacimiento: row.querySelector('input[data-field="fechaNacimiento"]').value || null,
            esMenor: row.querySelector('input[data-field="esMenor"]').checked,
            esTitular: row.querySelector('input[data-field="esTitular"]').checked || idx === 0
        })
    })

    // Ensure at least one titular
    if (viajeros.length > 0 && !viajeros.some(v => v.esTitular)) {
        viajeros[0].esTitular = true
    }

    // Update lead and trip
    l.grupoViajeros = viajeros
    l.viajeros = `${viajeros.filter(v => !v.esMenor).length}A${viajeros.filter(v => v.esMenor).length > 0 ? ', ' + viajeros.filter(v => v.esMenor).length + 'M' : ''}`

    // Update client trip
    const c = clients[l.clientKey]
    if (c) {
        const t = c.trips.find(t => t.id === leadId)
        if (t) {
            t.grupoViajeros = viajeros
            t.viajeros = l.viajeros
        }
    }

    // Persist grupoViajeros to formData for Supabase JSONB
    if (!l.formData) l.formData = {}
    l.formData.grupoViajeros = viajeros

    window.syncLeadToSupabase(l)
    window.renderAll()
    window.closeModal('editViajerosModal')

    // Reopen panel to show updated info
    if (currentLeadId === leadId) {
        openPanel(leadId)
    }

    const cumples = viajeros.filter(v => v.fechaNacimiento).length
    window.toast(`${viajeros.length} viajero(s) guardado(s)${cumples > 0 ? ` • ${cumples} cumpleaños registrado(s)` : ''}`, 'success')
}

// ========== CLIENT DASHBOARD ("Mi Viaje") ==========

function buildDashboardPrefill(l) {
    return {
        titular: l.titular,
        destination: l.destination,
        type: l.type,
        travelDate: l.travelDate,
        travelEnd: l.travelEnd,
        hotel: l.hotels?.[0]?.name || l.hotel,
        viajeros: l.viajeros,
        adultos: l.formData?.adultos || l.adultos,
        menores: l.formData?.menores || l.menores,
        reservationNum: l.reservationNum,
        platform: l.platform,
        grupoViajeros: l.grupoViajeros || l.formData?.grupoViajeros,
        hotels: l.hotels,
        parkEntries: l.parkEntries,
        cruises: l.cruises
    }
}

async function createClientDashboard(leadId) {
    const l = store.leads.find(x => x.id === leadId) || store.historico.find(x => x.id === leadId)
    if (!l) return
    const supabaseId = l._supabaseId || l.id
    if (!supabaseId || supabaseId.length !== 36) {
        window.toast('Lead debe estar sincronizado con Supabase primero', 'error')
        return
    }

    // Check if already exists
    let dash = store._dashboardCache[leadId]
    if (!dash) {
        dash = await window.supabaseDB.fetchClientDashboard(supabaseId)
    }
    if (dash) {
        store._dashboardCache[leadId] = dash
        window.toast('Este lead ya tiene un dashboard', 'info')
        window.openLead(leadId)
        return
    }

    const prefill = buildDashboardPrefill(l)
    const data = await window.supabaseDB.createClientDashboard(supabaseId, prefill)
    if (data) {
        store._dashboardCache[leadId] = data
        window.toast('Dashboard "Mi Viaje" creado', 'success')
        window.openLead(leadId)
    } else {
        window.toast('Error creando dashboard', 'error')
    }
}

async function loadDashboardForLead(leadId) {
    if (store._dashboardCache[leadId]) return store._dashboardCache[leadId]
    const l = store.leads.find(x => x.id === leadId) || store.historico.find(x => x.id === leadId)
    if (!l) return null
    const supabaseId = l._supabaseId || l.id
    if (!supabaseId || supabaseId.length !== 36) return null
    const dash = await window.supabaseDB.fetchClientDashboard(supabaseId)
    if (dash) store._dashboardCache[leadId] = dash
    return dash
}

function renderDashboardSection(l) {
    const dash = store._dashboardCache[l.id]
    if (!dash) {
        // Try loading async — if found, re-open panel to show it
        const supabaseId = l._supabaseId || l.id
        if (supabaseId && supabaseId.length === 36 && !store._dashboardCache[l.id + '_loading']) {
            store._dashboardCache[l.id + '_loading'] = true
            window.supabaseDB.fetchClientDashboard(supabaseId).then(d => {
                delete store._dashboardCache[l.id + '_loading']
                if (d) {
                    store._dashboardCache[l.id] = d
                    // Re-render panel if this lead is still open
                    if (store.currentLeadId === l.id) window.openLead(l.id)
                }
            }).catch(() => { delete store._dashboardCache[l.id + '_loading'] })
        }
        return `<div class="panel-section">
            <button class="btn btn-sm" onclick="createClientDashboard('${l.id}')"
                style="width:100%;background:linear-gradient(135deg,#7c3aed,#ec4899);border:none;color:white;">
                🌐 Crear Dashboard "Mi Viaje"
            </button>
        </div>`
    }

    const url = 'https://crm.m2magic.com/mi-viaje/' + dash.token
    const timelineCount = (dash.timeline || []).filter(t => t.visible !== false).length
    const hasFeedback = !!dash.feedback_submitted_at
    const statusLabel = { active: 'Activo', completed: 'Completado', archived: 'Archivado' }
    const statusColor = { active: 'var(--green)', completed: '#06b6d4', archived: 'var(--text-3)' }

    return `<div class="panel-section" style="border-left:3px solid ${statusColor[dash.status] || 'var(--green)'};">
        <div class="panel-section-title" style="display:flex;justify-content:space-between;">
            <span>🌐 Dashboard Mi Viaje</span>
            <span style="color:${statusColor[dash.status]};font-size:11px;font-weight:600;">
                ${statusLabel[dash.status] || dash.status}${hasFeedback ? ' • Feedback recibido' : ''}
            </span>
        </div>
        <a href="${url}" target="_blank" rel="noopener" style="display:block;font-size:11px;color:var(--purple);margin:6px 0;word-break:break-all;text-decoration:none;opacity:0.8;"
            title="Abrir dashboard del cliente">${url}</a>
        <div style="font-size:11px;color:var(--text-3);margin:2px 0 8px;">${timelineCount} items en timeline${dash.viewed_at ? ' • Visto' : ''}</div>
        <div style="display:flex;gap:6px;">
            <button class="btn btn-sm btn-secondary" onclick="copyDashboardLink('${url}')" style="flex:1;">📋 Copiar Link</button>
            ${l.phone ? `<a href="${window.generarWhatsAppLink(l.phone, '¡Hola ' + (l.titular || '').split(' ')[0] + '! Aquí puedes ver todo sobre tu viaje: ' + url)}"
                target="_blank" class="btn btn-sm" style="flex:1;background:var(--green);color:white;border:none;text-decoration:none;text-align:center;">💬 WhatsApp</a>` : ''}
        </div>
        <div style="display:flex;gap:6px;margin-top:6px;">
            <button class="btn btn-sm btn-ghost" onclick="manageDashboardTimeline('${l.id}')" style="flex:1;">📝 Timeline</button>
            ${hasFeedback ? `<button class="btn btn-sm btn-ghost" onclick="viewDashboardFeedback('${l.id}')" style="flex:1;">📊 Ver Feedback</button>` : ''}
        </div>
    </div>`
}

function copyDashboardLink(url) {
    navigator.clipboard.writeText(url).then(() => window.toast('Link copiado', 'success'))
}

async function manageDashboardTimeline(leadId) {
    const dash = store._dashboardCache[leadId]
    if (!dash) return

    const timeline = dash.timeline || []
    let html = '<div style="max-height:70vh;overflow-y:auto;padding:4px 0;">'

    if (timeline.length === 0) {
        html += '<p style="color:var(--text-3);text-align:center;padding:20px;">Sin items en el timeline</p>'
    } else {
        timeline.forEach((item, idx) => {
            const typeIcons = { milestone: '🏁', document: '📄', form_link: '📝', meeting: '🎥', note: '💬' }
            const icon = typeIcons[item.type] || '📌'
            const dateStr = item.addedAt ? new Date(item.addedAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) : ''
            const visClass = item.visible === false ? 'opacity:0.4;' : ''
            html += `<div style="display:flex;align-items:center;gap:8px;padding:8px;background:var(--bg-2);border-radius:var(--radius);margin-bottom:6px;${visClass}">
                <span style="font-size:16px;">${icon}</span>
                <div style="flex:1;min-width:0;">
                    <div style="font-size:13px;font-weight:600;">${window.escHtml(item.data?.title || item.key)}</div>
                    ${item.data?.description ? `<div style="font-size:11px;color:var(--text-3);">${window.escHtml(item.data.description)}</div>` : ''}
                    <div style="font-size:10px;color:var(--text-3);">${dateStr}</div>
                </div>
                <label style="display:flex;align-items:center;gap:4px;cursor:pointer;">
                    <input type="checkbox" ${item.visible !== false ? 'checked' : ''} onchange="toggleDashTimelineItem('${leadId}',${idx},this.checked)" />
                    <span style="font-size:10px;color:var(--text-3);">Visible</span>
                </label>
            </div>`
        })
    }

    html += '</div>'
    html += `<div style="display:flex;gap:6px;margin-top:12px;flex-wrap:wrap;">
        <button class="btn btn-sm btn-secondary" onclick="addDashTimelineItem('${leadId}','meeting')" style="flex:1;">🎥 Reunión</button>
        <button class="btn btn-sm btn-secondary" onclick="addDashTimelineItem('${leadId}','document')" style="flex:1;">📄 Documento</button>
        <button class="btn btn-sm btn-secondary" onclick="addDashTimelineItem('${leadId}','note')" style="flex:1;">💬 Nota</button>
    </div>`

    window.openModal('genericModal')
    document.getElementById('genericModal').querySelector('.modal-head h2').textContent = '📝 Timeline del Dashboard'
    document.getElementById('genericModal').querySelector('.modal-body').innerHTML = html
}

async function toggleDashTimelineItem(leadId, idx, visible) {
    const dash = store._dashboardCache[leadId]
    if (!dash) return
    const timeline = [...(dash.timeline || [])]
    if (timeline[idx]) {
        timeline[idx] = { ...timeline[idx], visible }
        const updated = await window.supabaseDB.updateDashboardTimeline(dash.id, timeline)
        if (updated) {
            store._dashboardCache[leadId] = updated
            window.toast(visible ? 'Item visible' : 'Item oculto', 'success')
        }
    }
}

function addDashTimelineItem(leadId, type) {
    const dash = store._dashboardCache[leadId]
    if (!dash) return

    let title = '', description = '', extraHtml = ''
    if (type === 'meeting') {
        title = 'Reunión de Dudas'
        extraHtml = `<div style="margin-top:8px;">
            <label style="font-size:12px;color:var(--text-2);">Fecha y hora</label>
            <input type="datetime-local" id="dashItemDate" class="input-field" />
            <label style="font-size:12px;color:var(--text-2);margin-top:6px;display:block;">Link (Zoom/Meet)</label>
            <input type="text" id="dashItemLink" class="input-field" placeholder="https://zoom.us/j/..." />
        </div>`
    } else if (type === 'document') {
        title = ''
        extraHtml = `<div style="margin-top:8px;">
            <label style="font-size:12px;color:var(--text-2);">Nombre del documento</label>
            <input type="text" id="dashItemDocName" class="input-field" placeholder="Itinerario, Guía, etc." />
            <label style="font-size:12px;color:var(--text-2);margin-top:6px;display:block;">Archivo</label>
            <input type="file" id="dashItemFile" accept=".pdf,.docx,.jpg,.png" />
        </div>`
    } else if (type === 'note') {
        title = ''
        extraHtml = `<div style="margin-top:8px;">
            <label style="font-size:12px;color:var(--text-2);">Nota</label>
            <textarea id="dashItemNote" class="input-field" rows="3" placeholder="Mensaje para el cliente..."></textarea>
        </div>`
    }

    const html = `<div>
        <label style="font-size:12px;color:var(--text-2);">Título</label>
        <input type="text" id="dashItemTitle" class="input-field" value="${window.escHtml(title)}" />
        ${extraHtml}
        <button class="btn btn-primary btn-sm" onclick="saveDashTimelineItem('${leadId}','${type}')" style="width:100%;margin-top:12px;">Agregar</button>
    </div>`

    window.openModal('genericModal')
    document.getElementById('genericModal').querySelector('.modal-head h2').textContent = type === 'meeting' ? '🎥 Agregar Reunión' : type === 'document' ? '📄 Agregar Documento' : '💬 Agregar Nota'
    document.getElementById('genericModal').querySelector('.modal-body').innerHTML = html
}

async function saveDashTimelineItem(leadId, type) {
    const dash = store._dashboardCache[leadId]
    if (!dash) return

    const title = document.getElementById('dashItemTitle')?.value?.trim()
    if (!title) { window.toast('Ingresa un título', 'error'); return }

    const item = {
        key: type + '_' + Date.now(),
        type,
        visible: true,
        data: { title }
    }

    if (type === 'meeting') {
        const date = document.getElementById('dashItemDate')?.value
        const link = document.getElementById('dashItemLink')?.value?.trim()
        if (date) item.data.date = date
        if (link) item.data.link = link
    } else if (type === 'document') {
        const fileInput = document.getElementById('dashItemFile')
        const file = fileInput?.files?.[0]
        if (file) {
            const l = store.leads.find(x => x.id === leadId) || store.historico.find(x => x.id === leadId)
            const supabaseId = l?._supabaseId || l?.id
            if (supabaseId) {
                window.toast('Subiendo documento...', 'info')
                const publicUrl = await window.supabaseDB.uploadClientDocument(supabaseId, file, file.name)
                if (publicUrl) {
                    item.data.url = publicUrl
                    item.data.filename = file.name
                } else {
                    window.toast('Error subiendo archivo', 'error')
                    return
                }
            }
        }
    } else if (type === 'note') {
        const note = document.getElementById('dashItemNote')?.value?.trim()
        if (note) item.data.description = note
    }

    const updated = await window.supabaseDB.addDashboardTimelineItem(dash.id, item)
    if (updated) {
        store._dashboardCache[leadId] = updated
        window.toast('Item agregado al timeline', 'success')
        window.closeModal('genericModal')
        window.renderAll()
    }
}

async function viewDashboardFeedback(leadId) {
    const dash = store._dashboardCache[leadId]
    if (!dash?.feedback) { window.toast('Sin feedback aún', 'info'); return }

    const fb = dash.feedback
    let html = '<div style="max-height:70vh;overflow-y:auto;padding:4px 0;">'

    if (fb.submittedAt || dash.feedback_submitted_at) {
        const date = new Date(fb.submittedAt || dash.feedback_submitted_at)
        html += `<div style="text-align:center;margin-bottom:12px;color:var(--text-3);font-size:12px;">Recibido: ${date.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}</div>`
    }

    // Ratings
    const ratingLabels = {
        planificacion: 'Planificación',
        opciones: 'Opciones presentadas',
        proveedores: 'Proveedores',
        comunicacion: 'Comunicación',
        acompanamiento: 'Acompañamiento',
        velocidad: 'Velocidad de respuesta',
        recontrataria: '¿Recontrataría?'
    }
    const ratings = Object.entries(ratingLabels).filter(([k]) => fb[k] != null)
    if (ratings.length > 0) {
        html += '<div class="panel-section"><div class="panel-section-title">⭐ Calificaciones</div>'
        ratings.forEach(([key, label]) => {
            const val = fb[key]
            const color = val >= 8 ? 'var(--green)' : val >= 5 ? '#f59e0b' : '#ef4444'
            html += `<div style="display:flex;justify-content:space-between;padding:4px 0;font-size:13px;">
                <span style="color:var(--text-2);">${label}</span>
                <span style="font-weight:700;color:${color};">${val}/10</span>
            </div>`
        })
        html += '</div>'
    }

    // Multiple choice
    if (fb.claridadInfo) {
        html += `<div class="panel-section"><div class="panel-section-title">📋 Calidad del Servicio</div>
            <p style="font-size:13px;color:var(--text-2);">Claridad de la info: <strong>${window.escHtml(fb.claridadInfo)}</strong></p>
            ${fb.manejoInconvenientes ? `<p style="font-size:13px;color:var(--text-2);">Manejo de inconvenientes: <strong>${window.escHtml(fb.manejoInconvenientes)}</strong></p>` : ''}
        </div>`
    }

    // Loyalty
    if (fb.recomendaria) {
        html += `<div class="panel-section"><div class="panel-section-title">💜 Lealtad</div>
            <p style="font-size:13px;color:var(--text-2);">¿Recomendaría?: <strong>${window.escHtml(fb.recomendaria)}</strong></p>
        </div>`
    }

    // Open text
    if (fb.mejorar || fb.otroComentario) {
        html += '<div class="panel-section"><div class="panel-section-title">💬 Comentarios</div>'
        if (fb.mejorar) html += `<p style="font-size:13px;color:var(--text-2);font-style:italic;">"${window.escHtml(fb.mejorar)}"</p>`
        if (fb.otroComentario) html += `<p style="font-size:13px;color:var(--text-2);font-style:italic;margin-top:6px;">"${window.escHtml(fb.otroComentario)}"</p>`
        html += '</div>'
    }

    html += '</div>'

    window.openModal('genericModal')
    document.getElementById('genericModal').querySelector('.modal-head h2').textContent = '📊 Feedback del Cliente'
    document.getElementById('genericModal').querySelector('.modal-body').innerHTML = html
}

// Auto-upload to dashboard timeline after PDF generation
async function autoUploadToDashboard(leadId, blob, filename, title, type = 'document') {
    const l = store.leads.find(x => x.id === leadId) || store.historico.find(x => x.id === leadId)
    if (!l) return
    const supabaseId = l._supabaseId || l.id
    if (!supabaseId || supabaseId.length !== 36) return

    // Check if dashboard exists
    let dash = store._dashboardCache[leadId]
    if (!dash) {
        dash = await window.supabaseDB.fetchClientDashboard(supabaseId)
        if (dash) store._dashboardCache[leadId] = dash
    }
    if (!dash) return // No dashboard for this lead

    // Upload document to storage
    const publicUrl = await window.supabaseDB.uploadClientDocument(supabaseId, blob, filename)
    if (!publicUrl) return

    // Add timeline item
    const item = {
        key: type + '_' + Date.now(),
        type,
        visible: true,
        data: { title, url: publicUrl, filename }
    }
    const updated = await window.supabaseDB.addDashboardTimelineItem(dash.id, item)
    if (updated) {
        store._dashboardCache[leadId] = updated
        window.toast?.('Documento subido al dashboard del cliente', 'success')
    }
}

Object.assign(window, {
    generateGuideToken, determineParkCodes, extractMinorAges,
    _guideCounter, generateGuideLink, confirmGenerateGuide,
    copyGuideLink, renderGuideSection, viewGuideResponses,
    fetchParkRecommendation, recalcParkRecommendation, copyParkRecommendation, whatsappParkRecommendation,
    renderFormDataPanel,
    openClient, closePanel, openPanel,
    openTripView,
    saveTeamMember, editTeamMember, saveTeamEdit, deleteTeamMember,
    openEditViajeros, renderViajerosList, addViajeroRow,
    removeViajeroRow, updateTitular, calcAgeFromBirth,
    updateMenorStatus, saveViajeros,
    // Client Dashboard
    createClientDashboard, loadDashboardForLead,
    renderDashboardSection, copyDashboardLink,
    manageDashboardTimeline, toggleDashTimelineItem,
    addDashTimelineItem, saveDashTimelineItem,
    viewDashboardFeedback, autoUploadToDashboard,
})
